import { EventRecord } from '@shared/core/events'
import { FC, useCallback, useContext, useMemo, useState } from 'react'
import { DateContext } from 'src/modules/core/context'
import { EditEventModal, EventList } from 'src/modules/event/components'
import { eventPreviewFields } from 'src/modules/event/constants/event-params'
import { useEventById } from 'src/modules/event/hooks/use-event-by-id'
import { useActivatingEventListPoll } from 'src/modules/event/hooks/use-activating-event-list-poll'
import { useUpdateEvent } from 'src/modules/event/hooks/use-update-event'
import type { EventData, ExtendEventPreview } from 'src/modules/event/types/event.types'
import { useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import type { DashboardTabProps } from 'src/modules/user/types/dashboard'

export const EnabledRemindersTab: FC<DashboardTabProps> = ({ isLoading }) => {
  const { setSelectedDate, startDate, endDate } = useContext(DateContext)

  const params = useMemo(
    () => ({
      fields: eventPreviewFields,
      start_date: startDate,
      end_date: endDate,
    }),
    [startDate, endDate],
  )

  const eventList = useActivatingEventListPoll(
    params,
    {
      enabled: !isLoading,
    },
    (event) => event.active,
  )
  const profile = useProfile()
  const preferences = useUserPreferences()
  const updateEvent = useUpdateEvent(params)

  const [editableEventPreview, setEditableEventPreview] = useState<ExtendEventPreview>(null)

  const eventDetails = useEventById(editableEventPreview?.parentRecurringId ?? editableEventPreview?.id)

  const isEventDetailsLoading = eventDetails.isLoading || eventDetails.isRefetching

  const activeEventList = useMemo(() => eventList.data?.filter((event) => event.active) || [], [eventList.data])

  const editableEvent = useMemo<EventData>(() => {
    if (!editableEventPreview || !eventDetails.data) return null

    return { ...editableEventPreview, ...eventDetails.data }
  }, [editableEventPreview, eventDetails.data])

  const handleSave = useCallback(
    (id: string, data: Partial<EventRecord>) => {
      updateEvent.mutate(data)
      setEditableEventPreview(null)
    },
    [updateEvent],
  )

  const onEventClick = useCallback((eventPreview: ExtendEventPreview) => {
    setEditableEventPreview(eventPreview)
  }, [])

  const onSwitchChange = useCallback(
    (event: EventData) => {
      updateEvent.mutate({
        id: event.parentRecurringId ?? event.id,
        active: event.active,
      })
    },
    [updateEvent],
  )

  return (
    <>
      <EventList
        events={activeEventList}
        truncated={eventList.data?.truncated}
        onCardClick={onEventClick}
        onSwitchChange={onSwitchChange}
        changeSelectedDate={setSelectedDate}
        isLoading={eventList.isLoading || isLoading}
      />
      {(editableEvent || isEventDetailsLoading) && (
        <EditEventModal
          profileData={profile.data}
          preferences={preferences.data}
          event={editableEvent}
          recentlyActivatedEventId={editableEventPreview?.id}
          onClose={() => setEditableEventPreview(null)}
          handleSave={handleSave}
          isLoading={isEventDetailsLoading}
        />
      )}
    </>
  )
}
